import { useMemo } from 'react'
import ReactMarkdown, { type Components } from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'

interface MarkdownContentProps {
  children: string
  /** Optional: erlaubt Callern, eigene Component-Overrides anzuhaengen (z.B. blockquote). */
  components?: Partial<Components>
}

/**
 * Wandelt `[[vault-pfad|Anzeige]]`-Wikilinks in interne Markdown-Links um
 * (Deep-Link auf die Quellen-Seite). Bleibt Markdown — kein rohes HTML.
 */
function preprocessWikilinks(content: string): string {
  return content.replace(
    /\[\[([^\]|]+?)(?:\|([^\]]+))?\]\]/g,
    (_match, target: string, display?: string) =>
      `[${display ?? target}](/quellen?path=${encodeURIComponent(target.trim())})`,
  )
}

/**
 * Standard-Markdown-Renderer mit External-Link-Behandlung:
 * - Wikilinks (`[[...]]`) werden als interne Deep-Links auf `/quellen?path=...`
 *   gerendert (client-side via react-router Link).
 * - Andere In-App-Links ('/...') werden ebenfalls client-side navigiert,
 *   Anker ('#...') bleiben plain <a>.
 * - Alle externen Links (http/https/mailto/etc.) bekommen target="_blank" +
 *   rel="noopener noreferrer" + ein kleines External-Link-Glyph.
 */
export function MarkdownContent({ children, components }: MarkdownContentProps) {
  const processed = useMemo(() => preprocessWikilinks(children), [children])
  const merged: Partial<Components> = {
    a({ href, children: linkChildren, node: _node, ...props }) {
      if (!href) return <>{linkChildren}</>
      if (href.startsWith('/')) {
        return (
          <Link
            to={href}
            className="text-primary underline decoration-current/40 underline-offset-2 hover:decoration-current break-all"
          >
            {linkChildren}
          </Link>
        )
      }
      if (href.startsWith('#')) {
        return (
          <a
            href={href}
            className="text-primary underline decoration-current/40 underline-offset-2 hover:decoration-current break-all"
            {...props}
          >
            {linkChildren}
          </a>
        )
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex max-w-full items-baseline gap-0.5 text-primary underline decoration-current/40 underline-offset-2 hover:decoration-current"
          {...props}
        >
          <span className="min-w-0 break-all">{linkChildren}</span>
          <ExternalLink className="h-3 w-3 shrink-0 self-center opacity-60" aria-hidden="true" />
        </a>
      )
    },
    // Gemeinsame Lesetypografie: Tabellen und Code-Bloecke duerfen die Seite
    // nicht ueberlaufen, sondern scrollen lokal.
    table({ children, node: _node, ...props }) {
      return (
        <div className="max-w-full overflow-x-auto">
          <table className="w-full border-collapse text-[14px] leading-[1.45]" {...props}>
            {children}
          </table>
        </div>
      )
    },
    pre({ children, node: _node, ...props }) {
      return (
        <pre className="max-w-full overflow-x-auto rounded-md bg-muted p-3 font-mono text-[13px] leading-relaxed" {...props}>
          {children}
        </pre>
      )
    },
    img({ src, alt, node: _node, ...props }) {
      return (
        <img
          src={src}
          alt={alt ?? ''}
          className="max-w-full h-auto"
          loading="lazy"
          {...props}
        />
      )
    },
    li({ children, node: _node, ...props }) {
      return (
        <li className="min-w-0 break-words" {...props}>
          {children}
        </li>
      )
    },
    p({ children, node: _node, ...props }) {
      return (
        <p className="min-w-0 break-words" {...props}>
          {children}
        </p>
      )
    },
    ...components,
  }
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={merged}>
      {processed}
    </ReactMarkdown>
  )
}
